import type { Offer } from "@booking/booking/api/offer/types";
import { nightsOnOffer } from "@booking/booking/api/offer/utils";
import {
  buildSimpleTeeTimesOptions,
  getSimpleTeeTimeSlotOffset,
  teeTimeDefaultValue,
} from "@booking/booking/api/simpleTeeTimes/utils";
import { DATE_FORMATS } from "@booking/consts";
import { type Maybe } from "@booking/types";
import { addDays, format, formatISO, isWeekend, setHours } from "date-fns";
import { type StateCreator } from "zustand";

import {
  type SimpleTeeTimesState,
  SimpleTimeSlot,
  SimpleTimeSlots,
} from "./types";

const teeTimesOptions = buildSimpleTeeTimesOptions();

export const createSimpleTeeTimesState: StateCreator<SimpleTeeTimesState> = (
  set,
  get,
) => ({
  simpleTeeTimes: [],
  dateOptions: [],

  simpleTeeTimesActions: {
    preselectSimpleTeeTimes: (arrivalDate: Maybe<Date>, offer?: Offer) => {
      if (!offer || !arrivalDate) {
        return;
      }

      const result: SimpleTimeSlots = [];
      let day = 0;

      function buildDate(): Date {
        const date = addDays(arrivalDate as Date, day);
        day += 1;
        return setHours(date, teeTimeDefaultValue(arrivalDate as Date, date));
      }

      offer.roundAllocation.allocated.map((round) => {
        [...Array(round.rounds)].forEach(() => {
          result.push({
            courses: [round.course],
            date: buildDate(),
            selectedCourse: round.course,
          });
        });
      });

      const unallocated = offer.roundAllocation.unallocated;
      if (unallocated) {
        [...Array(unallocated.rounds)].forEach(() => {
          result.push({
            courses: unallocated.courses,
            date: buildDate(),
            selectedCourse: unallocated.courses[0],
          });
        });
      }

      const unlimitedCourses = offer.roundAllocation.unlimited;
      if (unlimitedCourses.length) {
        const nights = nightsOnOffer(offer) || 0;
        const unlimitedRoundCount = nights;
        [...Array(unlimitedRoundCount)].forEach(() => {
          result.push({
            courses: unlimitedCourses,
            date: buildDate(),
            selectedCourse: unlimitedCourses[0],
          });
        });
      }

      const totalNights = nightsOnOffer(offer) || 0;
      const newDateOptions = [];
      // The stay is for `totalNights`, which covers `totalNights + 1` days.
      for (let i = 0; i <= totalNights; i++) {
        const optionDate = addDays(arrivalDate, i);
        newDateOptions.push({
          value: format(optionDate, "yyyy-MM-dd"),
          label: format(optionDate, DATE_FORMATS.SHORT_ORDINAL_DAY_SHORT_MONTH),
        });
      }

      set({ simpleTeeTimes: result, dateOptions: newDateOptions });
    },

    setCourse: (idx: string, value: string) => {
      const index = parseInt(idx, 10);
      set({
        simpleTeeTimes: get().simpleTeeTimes.map((teeTime, i) => {
          if (i !== index) {
            return teeTime;
          }
          const newCourse = teeTime.courses.find(
            (course) => course.id.toString() === value,
          );
          return {
            ...teeTime,
            selectedCourse: newCourse || teeTime.selectedCourse,
          };
        }),
      });
    },

    setDate: (idx: string, value: string) => {
      const index = parseInt(idx, 10);
      set({
        simpleTeeTimes: get().simpleTeeTimes.map((teeTime, i) => {
          if (i !== index) {
            return teeTime;
          }
          // The value is an ISO date string (YYYY-MM-DD)
          const newDate = new Date(`${value}T00:00:00`);

          // Preserve the time from the original date
          newDate.setHours(teeTime.date.getHours());
          newDate.setMinutes(teeTime.date.getMinutes());
          newDate.setSeconds(teeTime.date.getSeconds());
          newDate.setMilliseconds(teeTime.date.getMilliseconds());

          return { ...teeTime, date: newDate };
        }),
      });
    },

    setTime: (idx: string, value: string) => {
      const index = parseInt(idx, 10);
      set({
        simpleTeeTimes: get().simpleTeeTimes.map((teeTime, i) =>
          i === index
            ? { ...teeTime, date: setHours(teeTime.date, parseInt(value, 10)) }
            : teeTime,
        ),
      });
    },
  },

  simpleTeeTimesUtils: {
    buildTimeOptions: (needRestriction?: boolean) => {
      const { defaultOptions, weekendOptions, arrivalOptions } =
        teeTimesOptions;
      return get().simpleTeeTimes.map((timeSlot, idx) => {
        if (needRestriction) {
          if (!idx) return arrivalOptions;

          if (isWeekend(timeSlot.date)) return weekendOptions;
        }

        return defaultOptions;
      }, {});
    },
    encodeTimeSlots: (no_players: number) => {
      return get().simpleTeeTimes.map((timeSlot: SimpleTimeSlot) => {
        return {
          course_id: timeSlot.selectedCourse.id,
          tee_times: [
            {
              no_players,
              tee_time_at: formatISO(timeSlot.date),
              gap_mins: getSimpleTeeTimeSlotOffset(timeSlot.date) * 60,
            },
          ],
        };
      });
    },
  },
});
